import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HS_DEFAULT_VIEWER_FILTER,
  HS_DEFAULT_VIEWER_SORTER,
  HsDefaultMaterial,
  HsGraphicLayer,
  HsGraphicLines,
  HsGraphicObject,
  HsGraphicPoints,
  HsGraphicQueue,
  HsPointsMaterial,
  HsRegularGeometryData,
  HsRenderObjects,
  HsShaderData,
  HsShaderObjectType,
  hsGenerateCubeGeometryData,
  hsIndexGeometry,
  hsMakeGeometry,
  hsMakeIndexedGeometry,
  type HsDefaultShader,
  type HsGeometry,
  type HsGraphics,
  type HsHypersceneViewer,
  type HsMaterial,
  type HsRegularGeometry,
  type HsSamples,
  type HsLightStorage,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { makeLitMaterial } from '../../functions/make-lit-material.function';
import { makePinkPointLights } from '../../functions/make-pink-point-lights.function';

const CAMERA_POSITION: [number, number, number] = [2, 10, 30];
const GROUND_SIZE: number = 40;
const POINT_CLOUD_SIDE: number = 20;
const LINE_SEGMENTS: number = 100;

/**
 * Two layers with their own depth buffers and antialiasing: single objects (lines, points, cubes) and repeated
 * objects (ground, line and point rows). The top layer draws over the bottom one regardless of depth.
 */
@Component({
  selector: 'hyperscene-layers',
  templateUrl: './layers.component.html',
  styleUrls: ['./layers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class LayersDemo extends DemoViewerPage {
  public readonly samplesArray: HsSamples[] = [0, 1, 2, 3, 4, 5, 6, 7, 8];

  public activeSamples: HsSamples = 4;

  private readonly lights: HsLightStorage = makePinkPointLights();
  private readonly singleObjects: HsGraphicObject[] = [];
  private readonly repeatedObjects: HsGraphicObject[] = [];
  private topQueue: HsGraphicQueue | null = null;
  private topObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer> | null = null;
  private repeatedObjectsOnTop: boolean = false;
  private points: HsGraphicPoints | null = null;

  protected initScene(viewer: HsHypersceneViewer): void {
    const gl: WebGL2RenderingContext = viewer.gl;
    const graphics: HsGraphics = viewer.surface.graphics;
    // The viewer's scene layer is the bottom layer; the page draws the top layer after it.
    const topLayer: HsGraphicLayer = new HsGraphicLayer(viewer.camera, gl, this.activeSamples);
    topLayer.resizeFramebuffer(gl, gl.drawingBufferWidth, gl.drawingBufferHeight);
    // The top layer clears to transparent, so the bottom layer shows where it draws nothing.
    topLayer.transparentBackground = true;
    viewer.overlayLayers.push(topLayer);
    this.topQueue = new HsGraphicQueue(getUuid(), topLayer);
    this.topObjects = new HsRenderObjects(topLayer);

    this.makeSingleObjects(graphics, gl);
    this.makeRepeatedObjects(graphics, gl);
    this.applyLayerOrder();

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.setPosition(CAMERA_POSITION);
    viewer.onFrame(() => this.drawTopLayer());
  }

  protected override animate(): boolean {
    const time: number = Date.now() * 0.001;
    this.points?.setPointSize(20 + Math.pow(Math.sin(time) * 0.5 + 0.5, 2) * 10);
    return false;
  }

  public selectedSamples(samples: HsSamples): boolean {
    return samples === this.activeSamples;
  }

  public setActiveSamples(samples: HsSamples): void {
    this.activeSamples = samples;
    if (this.viewer === null || this.topObjects === null) {
      return;
    }
    const gl: WebGL2RenderingContext = this.viewer.gl;
    const supportedSamples: HsSamples = Math.min(samples, gl.getParameter(gl.MAX_SAMPLES)) as HsSamples;
    [this.viewer.surface.objectsLayer, this.topObjects.layer].forEach((layer: HsGraphicLayer) => {
      layer.antialiasingSamples = supportedSamples;
      layer.updateSamplingFramebuffer(gl);
    });
    this.viewer.invalidate();
  }

  public refreshOrderLayers(): void {
    this.repeatedObjectsOnTop = !this.repeatedObjectsOnTop;
    this.applyLayerOrder();
  }

  /** Puts one object set into the viewer's queue (bottom layer) and the other into the top layer's queue. */
  private applyLayerOrder(): void {
    if (this.viewer === null || this.topQueue === null) {
      return;
    }
    const viewer: HsHypersceneViewer = this.viewer;
    const topQueue: HsGraphicQueue = this.topQueue;
    const top: HsGraphicObject[] = this.repeatedObjectsOnTop ? this.repeatedObjects : this.singleObjects;
    const bottom: HsGraphicObject[] = this.repeatedObjectsOnTop ? this.singleObjects : this.repeatedObjects;
    top.forEach((object: HsGraphicObject) => {
      viewer.queue.removeObject(object);
      topQueue.addObject(object);
    });
    bottom.forEach((object: HsGraphicObject) => {
      topQueue.removeObject(object);
      viewer.add(object);
    });
    viewer.invalidate();
  }

  private drawTopLayer(): void {
    if (this.viewer === null || this.topQueue === null || this.topObjects === null) {
      return;
    }
    const viewer: HsHypersceneViewer = this.viewer;
    this.topObjects.clear();
    HS_DEFAULT_VIEWER_FILTER.filter(this.topQueue, viewer.camera, this.topObjects);
    HS_DEFAULT_VIEWER_SORTER.sort(viewer.camera, this.topObjects);
    viewer.render.drawToFramebuffer(viewer.gl, viewer.camera, this.topObjects);
  }

  private makeSingleObjects(graphics: HsGraphics, gl: WebGL2RenderingContext): void {
    const lineGeometry: HsRegularGeometry | null = hsMakeGeometry(graphics, makeLineSphereData(LINE_SEGMENTS));
    if (lineGeometry !== null) {
      const line: HsGraphicLines = new HsGraphicLines(getUuid(), 'line', lineGeometry, makeUnlitMaterial(gl, 1, 0, 0));
      this.place(line, [0, 2, 0], 2);
      this.singleObjects.push(line);
    }

    const pointGeometry: HsRegularGeometry | null = hsMakeGeometry(graphics, makePointSphereData(null));
    if (pointGeometry !== null) {
      this.points = new HsGraphicPoints(getUuid(), 'points', pointGeometry, makePointsMaterial(gl));
      this.place(this.points, [0, 4, 0], 3.5);
      this.singleObjects.push(this.points);
    }

    const cubeData: HsRegularGeometryData = hsGenerateCubeGeometryData();
    const standardGeometry: HsGeometry | null = hsMakeGeometry(graphics, cubeData);
    if (standardGeometry !== null) {
      const cube: HsGraphicObject = new HsGraphicObject(
        getUuid(),
        'standard cube',
        standardGeometry,
        makeLitMaterial(gl, 1, 1, 1, this.lights)
      );
      this.place(cube, [-10, 2, 0], 2);
      this.singleObjects.push(cube);
    }
    const indexedGeometry: HsGeometry | null = hsMakeIndexedGeometry(graphics, hsIndexGeometry(cubeData));
    if (indexedGeometry !== null) {
      const cube: HsGraphicObject = new HsGraphicObject(
        getUuid(),
        'indexed cube',
        indexedGeometry,
        makeLitMaterial(gl, 1, 0.75, 0, this.lights)
      );
      this.place(cube, [-15, 2, 0], 2);
      this.singleObjects.push(cube);
    }
  }

  private makeRepeatedObjects(graphics: HsGraphics, gl: WebGL2RenderingContext): void {
    const cubeGeometry: HsGeometry | null = hsMakeIndexedGeometry(
      graphics,
      hsIndexGeometry(hsGenerateCubeGeometryData())
    );
    if (cubeGeometry !== null) {
      const lightMaterial: HsMaterial = makeLitMaterial(gl, 1, 1, 1, this.lights);
      const darkMaterial: HsMaterial = makeLitMaterial(gl, 0.3, 0.3, 0.3, this.lights);
      for (let i: number = 0; i < GROUND_SIZE; i++) {
        for (let j: number = 0; j < GROUND_SIZE; j++) {
          const material: HsMaterial = (i + j) % 2 === 1 ? darkMaterial : lightMaterial;
          const cube: HsGraphicObject = new HsGraphicObject(getUuid(), 'ground', cubeGeometry, material);
          this.place(cube, [i * 2 - GROUND_SIZE, -2, j * 2 - GROUND_SIZE], 1);
          this.repeatedObjects.push(cube);
        }
      }
    }

    const lineGeometry: HsRegularGeometry | null = hsMakeGeometry(graphics, makeLineSphereData(LINE_SEGMENTS));
    if (lineGeometry !== null) {
      const lineMaterial: HsMaterial = makeUnlitMaterial(gl, 1, 0.5, 0);
      for (let i: number = 0; i < 10; i++) {
        const line: HsGraphicLines = new HsGraphicLines(getUuid(), 'line', lineGeometry, lineMaterial);
        this.place(line, [i * 5 - 25, 2, -15], 1);
        this.repeatedObjects.push(line);
      }
    }

    const pointGeometry: HsRegularGeometry | null = hsMakeGeometry(graphics, makePointSphereData([1, 0.75, 0.25]));
    if (pointGeometry !== null) {
      const pointsMaterial: HsPointsMaterial<HsDefaultShader> = makePointsMaterial(gl);
      for (let i: number = 0; i < 10; i++) {
        const points: HsGraphicPoints = new HsGraphicPoints(getUuid(), 'points', pointGeometry, pointsMaterial);
        points.setPointSize(10);
        this.place(points, [i * 5 - 25, 3, -25], 1);
        this.repeatedObjects.push(points);
      }
    }
  }

  private place(object: HsGraphicObject, position: [number, number, number], scale: number): void {
    object.worldMatrix.scale([scale, scale, scale]);
    object.worldMatrix.setPosition(position);
    object.updateBoundingSphere();
  }
}

function makeUnlitMaterial(gl: WebGL2RenderingContext, red: number, green: number, blue: number): HsMaterial {
  const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
  shaderData.addColor().addDepth();
  const material: HsMaterial = new HsDefaultMaterial(gl, shaderData.makeShader());
  material.color.set(red, green, blue);
  return material;
}

function makePointsMaterial(gl: WebGL2RenderingContext): HsPointsMaterial<HsDefaultShader> {
  const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
  shaderData.addColor().addDepth().addPoints();
  return new HsPointsMaterial(gl, shaderData.makeShader());
}

/** Three circles of radius 2 as line segments, the legacy engine's line "sphere". */
function makeLineSphereData(segments: number): HsRegularGeometryData {
  const vertices: number[] = [];
  const circles: ((cos: number, sin: number) => number[])[] = [
    (cos: number, sin: number) => [cos * 2 + 1, 0, sin * 2],
    (cos: number, sin: number) => [cos * 2 + 1, sin * 2, 0],
    (cos: number, sin: number) => [1, cos * 2, sin * 2],
  ];
  circles.forEach((circle: (cos: number, sin: number) => number[]) => {
    for (let i: number = 0; i < segments; i++) {
      const from: number = (i / segments) * Math.PI * 2;
      const to: number = ((i + 1) / segments) * Math.PI * 2;
      vertices.push(...circle(Math.cos(from), Math.sin(from)), ...circle(Math.cos(to), Math.sin(to)));
    }
  });
  return new HsRegularGeometryData(vertices, new Array<number>(vertices.length).fill(0));
}

/**
 * Grid points inside the unit sphere. Point geometry keeps colors where meshes keep normals; without a fixed color
 * the points are colored by their grid position.
 */
function makePointSphereData(color: [number, number, number] | null): HsRegularGeometryData {
  const vertices: number[] = [];
  const colors: number[] = [];
  for (let i: number = 0; i < POINT_CLOUD_SIDE; i++) {
    for (let j: number = 0; j < POINT_CLOUD_SIDE; j++) {
      for (let k: number = 0; k < POINT_CLOUD_SIDE; k++) {
        const x: number = (i / POINT_CLOUD_SIDE) * 2 - 1;
        const y: number = (j / POINT_CLOUD_SIDE) * 2 - 1;
        const z: number = (k / POINT_CLOUD_SIDE) * 2 - 1;
        if (x * x + y * y + z * z - 1 < 0) {
          vertices.push(x, y, z);
          colors.push(...(color ?? [i / POINT_CLOUD_SIDE, j / POINT_CLOUD_SIDE, k / POINT_CLOUD_SIDE]));
        }
      }
    }
  }
  return new HsRegularGeometryData(vertices, colors);
}
